/**
 * 后端 API 客户端。
 *
 * ## 为什么不用 supabase-js
 *
 * 迁移到 Django 后端后，认证方式从「JWT 存 localStorage」变成
 * 「HttpOnly session cookie」。这不是能顺手换掉的一个配置项 ——
 * supabase-js 的整套API（`.from().select().eq()` 那套链式查询、
 * `auth.getSession()`、`onAuthStateChange`）都建立在「自己管token」之上。
 * 继续用它就得让后端再实现一遍 PostgREST 与 GoTrue，那不如不迁。
 *
 * 所以这里提供一个形状相近但更薄的封装，让上层模块改动最小。
 *
 * ## 与 supabase 的行为差异（上层需要注意）
 *
 * | 能力| supabase-js | 本模块 |
 * |---|---|---|
 * | 会话存储 | localStorage 里的 JWT | HttpOnly cookie，JS 读不到 |
 * | 会话变化 | `onAuthStateChange` 推送 | `onSessionChange` 推送（本模块内） |
 * | 会话过期 | 自动用 refresh token 续期 | 30 天 cookie 到期，需重新登录 |
 * | 错误 | `{data, error}` 成对返回 | **抛异常**（try/catch） |
 *
 * 最后一行是最大的差异：supabase 的风格是"永不抛"，本模块抛。
 * 迁移时逐个调用点改写，正好借这个机会把"忘了检查 error 就用data"
 * 这类隐患消掉 —— 那种代码在 supabase 下会静默拿到 undefined。
 *
 * ## CSRF
 *
 * cookie 认证必须配CSRF 防护（Django 默认开启）。流程：
 *   1. token 就在 `csrftoken` cookie 里，**每次写请求现读**（见 `ensureCsrf`）；
 *      只有 cookie 里没有时才 GET /api/auth/csrf 领一个；
 *   2. 每个写请求带 `X-CSRFToken` 头；403 时清掉旧 cookie、重新领一次再发。
 * CSRF token 是**公开的**（它防的是跨站借用，不是防读），
 * 所以 JS 可以读它并放进头里 —— 这也是 Django 官方支持的做法。
 *
 * ⚠️ 别在模块里缓存 token：`login()` / `logout()` 都会 rotate 它，
 *    缓存下来的副本从那一刻起就是过期的（详见 `ensureCsrf` 的注释）。
 */

const API_BASE = (() => {
	// 生产与本地都走同源相对路径。vite dev 有proxy 转发 /api，
	// 所以这里不需要区分环境（原 supabase.ts 要区分是因为它直连 IP:端口）。
	return '/api'
})()

/** 写请求的方法集合 —— 这些需要带 CSRF 头 */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export class ApiError extends Error {
	readonly status: number
	readonly code: string

	constructor(message: string, status: number, code: string) {
		super(message)
		this.name = 'ApiError'
		this.status = status
		this.code = code
	}

	/** 未登录。上层用它把用户导向登录页而不是弹错误。 */
	get isNotLoggedIn(): boolean {
		return this.status === 401
	}
}

function readCookie(name: string): string {
	const hit = document.cookie
		.split('; ')
		.find(row => row.startsWith(name + '='))
	return hit ? decodeURIComponent(hit.slice(name.length + 1)) : ''
}

/**
 * 领 CSRF token。
 *
 * ## ⚠️ token 会在登录/退出后被服务端轮换掉
 *
 * Django 的 `login()` 会调 `rotate_token()` —— **换一个全新的
 * csrftoken cookie**（防会话固定攻击的标准做法）。退出同理：
 * `logout()` 走 `request.session.flush()`，session 换了新的，
 * 旧 token 也不再匹配。
 *
 * 所以「登录成功 → 紧接着发一个写请求」（比如退出登录）时，
 * 内存里缓存的副本就是**过期**的，写请求必然 403。
 *
 * 实测（2026-10-20）：登录后 cookie 里的 token 确实变了
 * （`7JR1YPc1…` → `Vo94MOdp…`），拿旧 token 请求 /auth/logout
 * 得 403、拿新 token 得 200。
 *
 * ## 修法
 *
 * token 存在 **cookie** 里，而 cookie 由浏览器自动跟着响应更新。
 * 所以每次要用的时候**现读 cookie**（`readCookie`），而不是读
 * 内存里的副本 —— 读 cookie 是同步的、零成本，比维护一份会过期的
 * 副本可靠得多。
 *
 * 只有当 cookie 里确实没有 token 时（第一次访问、用户清了站点数据）
 * 才去 GET /api/auth/csrf 领一个。
 *
 * ⚠️ 不要为此在模块级缓存 token —— 那正是这个 bug 的来源。
 */
async function ensureCsrf(): Promise<string> {
	// 现读 cookie：它才是唯一可信的来源（服务端每次 rotate 都会更新它）
	const fromCookie = readCookie('csrftoken')
	if (fromCookie) return fromCookie
	// cookie 里没有 → 领一个。多个写请求同时启动时只领一次
	// （否则首屏几个并发写请求会各发一次 GET /auth/csrf）。
	if (!csrfFetch) {
		csrfFetch = (async () => {
			try {
				await rawFetch('/auth/csrf')
			} catch {
				// 拿不到就让它空着，写请求会 403 —— 这比静默失败更容易排查
			}
		})().finally(() => { csrfFetch = null })
	}
	await csrfFetch
	return readCookie('csrftoken')
}

/** 正在进行的「领 token」请求，用于并发去重。用完即置空。 */
let csrfFetch: Promise<void> | null = null

async function rawFetch(path: string, init?: RequestInit): Promise<Response> {
	return fetch(API_BASE + path, {
		...init,
		// ⭐ cookie 认证的关键：不带这行浏览器不会发送 session cookie，
		// 表现为"明明登录了但接口都说未登录"
		credentials: 'include',
	})
}

/** 解析后端统一响应格式 {ok, data} / {ok:false, error, code} */
async function unwrap<T>(res: Response): Promise<T> {
	let body: any = null
	try {
		body = await res.json()
	} catch {
		// 非 JSON。Django 的 CSRF 校验失败页就是 HTML（403）——
		// 报「非 JSON 内容」等于让人猜谜，这里直接说清是什么。
		if (res.status === 403) {
			throw new ApiError(
				'请求被安全校验拦下（CSRF），请刷新页面后重试',
				res.status,
				'csrf_failed',
			)
		}
		// 其它非 JSON（nginx 502 页面、反代错误等）
		throw new ApiError(
			`服务器返回了非 JSON 内容（HTTP ${res.status}）`,
			res.status,
			'bad_response',
		)
	}
	if (!res.ok || !body?.ok) {
		throw new ApiError(
			body?.error || `请求失败（HTTP ${res.status}）`,
			res.status,
			body?.code || 'error',
		)
	}
	return body.data as T
}

async function request<T>(
	path: string,
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
	payload?: unknown,
): Promise<T> {
	/**
	 * 发一次请求。
	 *
	 * ⚠️ headers 在**函数体内**现造，不能提到外面复用 ——
	 *    重试时必须带上**新** token。提到外面的话重试还是发旧 token，
	 *    于是「403 → 重试 → 又 403」，用户看到的就是"要点两次才生效"。
	 *    （这正是 2026-10-20 首页退出按钮的真实故障。）
	 */
	const send = async () => {
		const headers: Record<string, string> = {}
		if (payload !== undefined) headers['Content-Type'] = 'application/json'
		if (WRITE_METHODS.has(method)) headers['X-CSRFToken'] = await ensureCsrf()
		return rawFetch(path, {
			method,
			headers,
			body: payload === undefined ? undefined : JSON.stringify(payload),
		})
	}

	let res = await send()
	// CSRF 失败时重试一次：token 可能刚好被服务端轮换过
	// （登录/退出都会 rotate），或浏览器清了 cookie。
	// 只重试一次 —— 若是权限问题，重试没有意义。
	if (res.status === 403 && WRITE_METHODS.has(method) && isCsrfFailure(res)) {
		// 强制重新领一次：清掉 cookie 里的旧值，让 ensureCsrf 走
		// GET /auth/csrf 那条路。
		clearCsrfCookie()
		res = await send()
	}
	return unwrap<T>(res)
}

/**
 * 这个 403 是不是「CSRF 校验没过」？
 *
 * 403 有两种，含义完全不同：
 *   · **Django 的 CSRF 失败页**（content-type: text/html）—— token
 *     不匹配/缺失。重试有意义：换新 token 再发一次。
 *   · **本项目自己的 403**（content-type: application/json，形如
 *     `{"ok": false, "error": "需要管理员权限"}`）—— 权限不够。
 *     重试毫无意义，白打一次请求。
 *
 * 用 content-type 区分：CSRF 失败发生在**中间件**里，压根没进视图，
 * 所以它是 Django 默认那个 HTML 页面；本项目的 403 都走 `fail()`
 * 返回 JSON。
 */
function isCsrfFailure(res: Response): boolean {
	const ct = res.headers.get('content-type') || ''
	return !ct.includes('application/json')
}

/**
 * 清掉浏览器里的 csrftoken cookie。
 *
 * 只在 403 重试前用一次：强制下一次 `ensureCsrf()` 走
 * 「重新领 token」那条路，而不是继续用 cookie 里的旧值。
 *
 * cookie 的匹配只看 name/path/domain（**不看 SameSite**），所以这里
 * 不必也不能猜服务端用的 SameSite —— 猜错了没意义，猜对了也没用。
 *
 * ⚠️ **但 `Secure` 要跟着当前页面协议走**：浏览器拒绝在 http 页面上
 *    写入/删除 `Secure` cookie（静默忽略，不报错）。若这里无条件写
 *    `Secure`，本地 `http://127.0.0.1` 调试时删除会失效，重试仍然
 *    拿着旧 token → 又 403。生产是 https，才会真的带上 `Secure`。
 */
function clearCsrfCookie() {
	const secure = location.protocol === 'https:' ? '; Secure' : ''
	document.cookie = `csrftoken=; Max-Age=0; path=/${secure}`
}

export const api = {
	get: <T>(path: string) => request<T>(path, 'GET'),
	post: <T>(path: string, payload?: unknown) => request<T>(path, 'POST', payload),
	patch: <T>(path: string, payload?: unknown) => request<T>(path, 'PATCH', payload),
	del: <T>(path: string) => request<T>(path, 'DELETE'),
}

// --------------------------------------------------------------------
// 会话
// --------------------------------------------------------------------

export type Credential = {
	kind: 'wechat' | 'email' | 'phone'
	label: string
	/** 掩码串：`a***@163.com` / `138****8000` / 微信昵称。**不是**可登录的标识 */
	hint: string
	bound_at: string
}

export type CurrentUser = {
	id: string
	email: string
	nickname: string
	avatar_url: string
	is_admin: boolean
	/** 已绑定的登录方式。老响应里可能没有，读的时候一律按空数组兜底 */
	credentials?: Credential[]
	/**
	 * 还没有邮箱/手机号这类"第二凭证"。
	 *
	 * 微信直登的新账号就是这个状态。丢了微信号等于丢了账号，所以登录后
	 * 要引导补一种；前端据此弹「完善账号」，不当作错误处理。
	 */
	need_credential?: boolean
	/**
	 * 套餐。`plan_id === 'plus'` 就是 Plus 会员（判定集中在 saas/plan.ts）。
	 *
	 * 老响应的这三个字段可能没有（后端升级前发的缓存）：一律按免费版兜底，
	 * 别用 `plan_id!` 硬取 —— 那会让升级前的会话在界面上报错。
	 */
	plan_id?: string
	plan_name?: string
	/** 云端曲谱上限（20 / 200），来自 plans 表 */
	max_scores?: number
	/**
	 * 会员到期时间（ISO 串）。免费用户为 null；管理员手工授予的长期档也是 null。
	 * 只用来显示"有效期至 …"，**不要**拿它判断是不是会员 —— 判定只用 plan_id
	 * （后端已经把"过期但还没降级"的账号算成 free 了）。
	 */
	plan_expires_at?: string | null
}

export type SessionUser = CurrentUser | null

type SessionListener = (user: CurrentUser | null) => void
const listeners = new Set<SessionListener>()
let cachedUser: CurrentUser | null = null
let loaded = false

function notify() {
	listeners.forEach(cb => cb(cachedUser))
}

/**
 * 取当前登录者。
 *
 * 第一次调用会真的发请求，之后走缓存 —— 首屏要等它，
 * 但渲染过程里反复调（比如 displayName）不该每次都往返。
 */
export async function getSessionUser(): Promise<SessionUser> {
	if (loaded) return cachedUser
	try {
		const data = await api.get<{ user: CurrentUser | null }>('/auth/me')
		cachedUser = data.user
	} catch {
		// 后端没起来 / 网络断：按未登录处理，不让整站白屏。
		// 静默降级是刻意的 —— 登录态只是页面上的一小块 UI。
		cachedUser = null
	}
	loaded = true
	notify()
	return cachedUser
}

/**
 * 订阅登录态变化（对应 supabase.auth.onAuthStateChange）。
 *
 * 会**立即回调一次当前态**，然后在登录/登出时回调 —— 与原 supabase 行为
 * 一致，省得调用方自己先取一次再订阅。
 */
export function onSessionChange(cb: SessionListener): () => void {
	listeners.add(cb)
	void getSessionUser().then(cb)
	return () => listeners.delete(cb)
}

/**
 * 登录/登出后手动刷新缓存（由 auth 模块调用）
 */
export function setSessionUser(user: SessionUser): void {
	cachedUser = user
	loaded = true
	notify()
}

/**
 * 丢弃缓存并重新向后端问一次当前登录者。
 *
 * ## 为什么必须有它
 *
 * 微信扫码登录是在**另一个窗口**完成的：用户扫完码之后，
 * 后端在新窗口里种下 session cookie，然后 302 回首页。用户原来
 * 那个窗口自始至终没有发生过任何请求 —— 它内存里的 `cachedUser`
 * 仍然是 null，顶栏也仍然是"未登录"。
 *
 * 而 `getSessionUser()` 只在 `loaded === false` 时才发请求，所以单纯
 * 再调一次它是没用的。这里显式把 `loaded` 打回 false，强制真的往返一次。
 *
 * 用它的地方：扫码窗口关闭后、原窗口要刷新登录态时（见 auth.ts 的
 * `refreshSessionAfterWechat`）。邮箱密码登录**不需要**它 —— 那条路径
 * 的响应里就带着 user，直接 `setSessionUser()` 更快，也少一次请求。
 */
export async function refreshSessionUser(): Promise<SessionUser> {
	loaded = false
	return getSessionUser()
}

/** 模块被热替换（vite HMR）时复位缓存，避免旧用户态残留 */
const hot = (import.meta as unknown as {hot?: {dispose(cb: () => void): void}}).hot
if (hot) {
	hot.dispose(() => {
		cachedUser = null
		loaded = false
		listeners.clear()
	})
}